import React, { useState, useEffect, useReducer } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import "../assets/css/sidebar.css";
import skyRPLogo from "../assets/images/sky-rp-logo.png";
import { endSessionBeacon } from "../utils/sessionClock";

const menuItems = [
  {
    /* /dashboard, not "/". The screen lives at /dashboard, so linking to the
       root meant every click bounced through a redirect and the NavLink then
       failed to match the URL it had just produced — the Dashboard row never
       highlighted while you were looking at the Dashboard. */
    path: "/dashboard",
    icon: "bi bi-grid-fill",
    color: "#3b82f6",
    label: "Dashboard",
    roles: ["SUPER_ADMIN", "ADMIN", "USER", "VIEWER", "TEAM_LEADER"],
  },
  {
    path: "/master",
    icon: "bi-sliders",
    color: "#6366f1",
    label: "Master",
    roles: ["SUPER_ADMIN", "ADMIN", "VIEWER", "TEAM_LEADER"],
    children: [
      {
        path: "/shift-group",
        label: "Shift Group"
      },

      {
        path: "/shift",
        label: "Shift"
      },
      {
        path: "/status",
        label: "Status"
      },
      {
        path: "/employee-master",
        label: "Employee Master",
        
      }
    ],
  },
  {
    path: "/employee-management",
    icon: "bi bi-person-plus-fill",
    color: "#14b8a6",
    label: "Attendance",
    roles: ["SUPER_ADMIN", "ADMIN", "USER", "VIEWER", "TEAM_LEADER"],
    children: [
      {
        path: "/employee-attendance",
        label: "Employee Attendance",
      },
      {
        path: "/leave-request",
        label: "Leave Request",
      },
      {
        path: "/manual-entry",
        label: "Manual Entry",
      },
      {
        path: "/approval",
        label: "Approval",
        roles: ["SUPER_ADMIN", "ADMIN", "VIEWER", "TEAM_LEADER"], // USER removed
      },
    ],
  },
  {
    path: "/quotation",
    icon: "bi bi-file-earmark-text",
    color: "#f5313159",
    label: "Quotation",
    localOnly: true,
    roles: ["SUPER_ADMIN", "ADMIN", "USER", "VIEWER", "TEAM_LEADER"],
  },
  {
  path: "/quotation-master",
  icon: "bi bi-journal-text",
  color: "#3a10f59d",
  label: "Quotation Master",
    localOnly: true,
  roles: ["SUPER_ADMIN", "ADMIN", "USER", "VIEWER", "TEAM_LEADER"],
  children: [
    {
      path: "/company",
      label: "Company",
    },
     {
      path: "/bank",
      label: "Bank",
    },
    {
      path: "/customer",
      label: "Customer",
    },
     {
      path: "/module",
      label: "Module",
    },
     {
      path: "/prize",
      label: "Prize",
    },
     {
      path: "/service",
      label: "Service",
    },
    { 
      path: "/terms",
      label: "Terms and Condititons",
    },
    {
      path: "/payment",
      label: "Payment Terms"
    },
    {
      path: "/tax",
      label: "Tax"
    }

  ]
  },
  {
    path: "/tasks",
    icon: "bi-list-check",
    color: "#f59e0b",
    label: "Tech Team Tasks",
    roles: ["SUPER_ADMIN", "ADMIN"],
  },
  {
    path: "/support-team-task",
    icon: "bi bi-people-fill",
    color: "#ec4899",
    label: "Common Team Task",
    roles: ["SUPER_ADMIN", "ADMIN", "USER", "VIEWER", "TEAM_LEADER"],
  },
  {
    path: "/task-assignment",
    icon: "bi-person-check",
    color: "#8b5cf6",
    label: "Task Assignment",
    roles: ["SUPER_ADMIN", "ADMIN", "TEAM_LEADER", "VIEWER"],
  },
  {
    path: "/development-tasks",
    icon: "bi-code-slash",
    color: "#06b6d4",
    label: "Development Tasks",
    roles: ["SUPER_ADMIN", "ADMIN", "TEAM_LEADER", "USER"],
  },
  {
    path: "/testting-tasks",
    icon: "bi-shield-check",
    color: "#10b981",
    label: "QA Tasks",
    roles: ["SUPER_ADMIN", "ADMIN", "VIEWER"],
  },
  {
    path: "/deployments-tasks",
    icon: "bi-server",
    color: "#ef4444",
    label: "Deployments",
    roles: ["SUPER_ADMIN", "ADMIN"],
  },
  {
    path: "/reports",
    icon: "bi bi-file-earmark-bar-graph-fill",
    color: "#f97316",
    label: "Over All Report",
    roles: ["SUPER_ADMIN", "ADMIN", "USER", "VIEWER", "TEAM_LEADER"],
  },
  {
    path: "/user-reports",
    icon: "bi bi-person-lines-fill",
    color: "#0ea5e9",
    label: "User Report",
    roles: ["SUPER_ADMIN", "ADMIN", "USER", "VIEWER", "TEAM_LEADER"],
  },
  {
    path: "/punching-report",
    icon: "bi bi-fingerprint",
    color: "#0891b2",
    label: "Punching Report",
    roles: ["SUPER_ADMIN", "ADMIN", "TEAM_LEADER", "USER", "VIEWER"],
  },
  {
    path: "/teams",
    icon: "bi bi-people-fill",
    color: "#7c3aed",
    label: "Teams",
    /* Creating and renaming a team reshapes the board for everybody, and
       POST /team is authorized for these two roles alone — a wider menu would
       offer a screen the server refuses to act on. */
    roles: ["SUPER_ADMIN", "ADMIN"],
  },
  {
    path: "/team-tree",
    icon: "bi-diagram-3-fill",
    color: "#7c3aed",
    label: "Team Tree",
    /* Everybody. The tree was admin-only while it owned the editing — a menu
       entry leading to controls the server would refuse is worse than no
       entry. It reads now: who is in which team, how loaded each one is, and
       where work flows next. That is worth knowing whatever your role, and it
       is the question people were asking each other instead.

       Changing any of it stays on Teams, above, which remains the two roles
       POST /team and POST /team/member are authorized for. */
    roles: ["SUPER_ADMIN", "ADMIN", "USER", "VIEWER", "TEAM_LEADER"],
  },
  {
    path: "/users",
    icon: "bi bi-person-badge",
    color: "#64748b",
    label: "Users",
    roles: ["SUPER_ADMIN", "ADMIN", "USER", "VIEWER", "TEAM_LEADER"],
  },
];

const getInitials = (name) => {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return String(name || "U").slice(0, 2).toUpperCase();
};

const prettyRole = (role) =>
  String(role || "User")
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

// Canonical roles the menu understands. Whatever shape the API returns the role
// in (casing, spacing, or a differently-named key), normalize it to one of these
// so a valid admin is never falsely locked out of their menus.
const ROLE_ALIASES = {
  SUPERADMIN: "SUPER_ADMIN",
  ADMIN: "ADMIN",
  USER: "USER",
  VIEWER: "VIEWER",
  TEAMLEADER: "TEAM_LEADER",
};

const normalizeRole = (raw) => {
  if (!raw) return "USER";
  const key = String(raw).trim().toUpperCase().replace(/[\s-]+/g, "_");
  return ROLE_ALIASES[key.replace(/_/g, "")] || key;
};

const readStoredUser = () => {
  try {
    return JSON.parse(localStorage.getItem("user") || "{}") || {};
  } catch {
    return {};
  }
};

const readUserRole = () => {
  const u = readStoredUser();
  return normalizeRole(
    u.role ?? u.Role ?? u.roleName ?? u.RoleName ?? u.userRole
  );
};

/* Are we on a developer's machine, or a deployed site?
   Read from where the PAGE is served, which is the only thing that reliably
   distinguishes the two: an API URL is baked in at build time, so a bundle
   built locally and uploaded would still claim to be local.

   (Not to be confused with the date handling this app used to do. That guessed
   the API server's clock from this same string, which it cannot know. Whether
   a menu is shown on a developer's machine is genuinely a fact about the page,
   so reading it here is right.) */
const isLocalEnvironment = () => {
  const h = window.location.hostname;
  return (
    h === "localhost" ||
    h === "127.0.0.1" ||
    h === "[::1]" ||
    h.endsWith(".local") ||
    /^\d+\.\d+\.\d+\.\d+$/.test(h) // a LAN address, e.g. testing from a phone
  );
};

/* What this account actually gets to see.
 *
 * `localOnly` hides an item on deployed sites while leaving it available while
 * developing — for a screen that is built but not ready for users yet.
 *
 * Roles hide too now, rather than showing a padlock. A locked row costs
 * attention every single day and can never be acted on: people click it and
 * nothing happens, and the ones who read the padlock go and ask for access to
 * a screen that was never meant for them. Neither reading helps anybody. A
 * menu should be the list of places you can go.
 *
 * A parent whose children are all hidden goes with them — an accordion that
 * opens onto nothing is worse than no accordion.
 *
 * If NOTHING matches, the role is one this menu has never heard of rather than
 * an account with no permissions, and an empty sidebar is not the answer to
 * that. Falls back to the USER menu, which is the same default normalizeRole
 * already applies to a MISSING role — one level later, for an unrecognised
 * one. */
const visibleMenu = (items, local, role) => {
  const forRole = (r) =>
    items
      .filter((item) => local || !item.localOnly)
      .filter((item) => !item.roles || item.roles.includes(r))
      .map((item) =>
        item.children
          ? {
              ...item,
              children: item.children.filter(
                (child) => !child.roles || child.roles.includes(r),
              ),
            }
          : item,
      )
      .filter((item) => !item.children || item.children.length > 0);

  const mine = forRole(role);
  return mine.length ? mine : forRole("USER");
};

const Sidebar = ({ isCollapsed, toggleMobileSidebar }) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Re-read auth whenever another tab, a window focus, or a tab becoming visible
  // could have updated the stored user — so a login / role change reflects here
  // WITHOUT a manual page refresh (the "menus disabled until I refresh" bug).
  const [, refreshAuth] = useReducer((x) => x + 1, 0);
  useEffect(() => {
    const sync = () => refreshAuth();
    window.addEventListener("storage", sync);
    window.addEventListener("focus", sync);
    document.addEventListener("visibilitychange", sync);
    return () => {
      window.removeEventListener("storage", sync);
      window.removeEventListener("focus", sync);
      document.removeEventListener("visibilitychange", sync);
    };
  }, []);

  const user = readStoredUser();
  const userRole = readUserRole();
  const menu = visibleMenu(menuItems, isLocalEnvironment(), userRole);
  const userName = user.userName || user.UserName || "User";

  // Auto-open the menu whose child route is currently active.
  const activeParentLabel =
    visibleMenu(menuItems, isLocalEnvironment(), userRole).find((m) =>
      m.children?.some((c) => c.path === location.pathname)
    )?.label || null;

  const [openMenu, setOpenMenu] = useState(activeParentLabel);

  const isChildActive = (item) =>
    item.children?.some((c) => c.path === location.pathname);

  const handleParentClick = (item) => {
    // No room for an inline accordion in the collapsed rail → jump to first child.
    if (isCollapsed) {
      navigate(item.children[0].path);
      return;
    }
    setOpenMenu((prev) => (prev === item.label ? null : item.label));
  };

  const handleLogout = () => {
    /* Say so before the token goes: the endpoint identifies the user from the
       body, and clearing storage first would leave nobody to name. */
    endSessionBeacon("logout");
    localStorage.removeItem("token");
    localStorage.removeItem("dashboard_animation_played");
    navigate("/login");
    window.location.reload();
  };

  return (
    <aside className={`sidebar ${isCollapsed ? "collapsed" : ""}`}>
      {/* Brand */}
      <div className="sidebar-header">
        <div className="brand">
          <span className="brand-logo">
            <img src={skyRPLogo} alt="SkyEye" />
          </span>
          <span className="brand-name">SkyEye</span>
        </div>
      </div>

      {/* Navigation */}
      <nav className="nav-scroll">
        <ul className="nav-links">
          {/* Everything in `menu` is reachable by this account — visibleMenu
              has already removed the rest — so there is no locked state to
              render and no click to swallow. */}
          {menu.map((item, index) => {
            // --- Menu with children (accordion) ---
            if (item.children) {
              const open = openMenu === item.label && !isCollapsed;
              const parentActive = isChildActive(item);

              return (
                <li key={index} className="menu-group">
                  <button
                    type="button"
                    className={`nav-item ${parentActive ? "active" : ""}`}
                    onClick={() => handleParentClick(item)}
                    title={isCollapsed ? item.label : undefined}
                    aria-expanded={open}
                  >
                    <span
                      className="nav-item__icon"
                      style={{ "--hue": item.color }}
                    >
                      <i className={`bi ${item.icon}`}></i>
                    </span>
                    <span className="nav-item__label">{item.label}</span>
                    <i
                      className={`bi bi-chevron-down nav-item__chevron ${open ? "rot" : ""
                        }`}
                    ></i>
                  </button>

                  <div className={`submenu ${open ? "open" : ""}`}>
                    <ul>
                      {/* {item.children.map((child, i) => (
                        <li key={i}>
                          <NavLink
                            to={child.path}
                            className={({ isActive }) =>
                              `submenu-link ${isActive ? "active" : ""}`
                            }
                            onClick={toggleMobileSidebar}
                          >
                            <span className="submenu-dot"></span>
                            <span className="submenu-text">{child.label}</span>
                          </NavLink>
                        </li>
                      ))} */}

                      {item.children.map((child, i) => (
                        <li key={i}>
                          <NavLink
                            to={child.path}
                            className={({ isActive }) =>
                              `submenu-link ${isActive ? "active" : ""}`
                            }
                            onClick={toggleMobileSidebar}
                          >
                            <span className="submenu-dot"></span>
                            <span className="submenu-text">{child.label}</span>
                          </NavLink>
                        </li>
                      ))}
                    </ul>
                  </div>
                </li>
              );
            }

            // --- Simple link ---
            return (
              <li key={index} className="menu-group">
                <NavLink
                  to={item.path}
                  end={item.path === "/"}
                  title={isCollapsed ? item.label : undefined}
                  className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
                  onClick={toggleMobileSidebar}
                >
                  <span
                    className="nav-item__icon"
                    style={{ "--hue": item.color }}
                  >
                    <i className={`bi ${item.icon}`}></i>
                  </span>
                  <span className="nav-item__label">{item.label}</span>
                </NavLink>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* User card */}
      <div className="sidebar-foot">
        <div className="side-user">
          <span className="side-avatar">{getInitials(userName)}</span>
          <span className="side-userinfo">
            <span className="side-uname">{userName}</span>
            <span className="side-urole">{prettyRole(userRole)}</span>
          </span>
          <button className="side-logout" title="Log out" onClick={handleLogout}>
            <i className="bi bi-box-arrow-right"></i>
          </button>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
